/**
 * Documents Tab - Quản lý tài liệu và upload lên File Search
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Popconfirm,
  Tag,
  Card,
  Upload,
  Select,
  Modal,
  Form,
  Input,
  Badge,
  Tooltip,
} from 'antd';
import {
  UploadOutlined,
  SyncOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import * as aiAssistantService from '../../services/aiAssistantService';

const DocumentsTab: React.FC = () => {
  const [documents, setDocuments] = useState<aiAssistantService.AIDocument[]>([]);
  const [collections, setCollections] = useState<aiAssistantService.AICollection[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<number | undefined>();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState<aiAssistantService.AIDocument | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    loadCollections();
    loadDocuments();
  }, [selectedCollection]);

  const loadCollections = async () => {
    try {
      const data = await aiAssistantService.listCollections();
      setCollections(data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await aiAssistantService.listDocuments(selectedCollection);
      setDocuments(data);
    } catch (error) {
      message.error('Không thể tải danh sách tài liệu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      const values = await uploadForm.validateFields();
      
      if (fileList.length === 0) {
        message.warning('Vui lòng chọn file');
        return;
      }

      const file = fileList[0].originFileObj as File;
      await aiAssistantService.uploadDocument(file, values.collectionId, values.title);
      
      message.success('Upload tài liệu thành công');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      setFileList([]);
      loadDocuments();
    } catch (error) {
      message.error('Không thể upload tài liệu');
      console.error(error);
    }
  };

  const handleSync = async (documentId: number) => {
    try {
      await aiAssistantService.syncDocumentToFileSearch(documentId);
      message.success('Đồng bộ tài liệu lên File Search thành công');
      loadDocuments();
    } catch (error) {
      message.error('Không thể đồng bộ tài liệu');
      console.error(error);
    }
  };

  const handleEdit = (record: aiAssistantService.AIDocument) => {
    setEditingDocument(record);
    editForm.setFieldsValue({
      title: record.Title,
      collectionId: record.CollectionID,
    });
    setEditModalVisible(true);
  };

  const handleEditOk = async () => {
    try {
      const values = await editForm.validateFields();
      if (editingDocument) {
        await aiAssistantService.updateDocument(editingDocument.DocumentID, values);
        message.success('Cập nhật tài liệu thành công');
        setEditModalVisible(false);
        loadDocuments();
      }
    } catch (error) {
      message.error('Không thể cập nhật tài liệu');
      console.error(error);
    }
  };

  const handleDelete = async (documentId: number) => {
    try {
      await aiAssistantService.deleteDocument(documentId);
      message.success('Xóa tài liệu thành công');
      loadDocuments();
    } catch (error) {
      message.error('Không thể xóa tài liệu');
      console.error(error);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'active':
        return <Tag icon={<CheckCircleOutlined />} color="success">Đã đồng bộ</Tag>;
      case 'pending':
        return <Tag icon={<ClockCircleOutlined />} color="default">Chưa đồng bộ</Tag>;
      case 'uploading':
        return <Tag icon={<SyncOutlined spin />} color="processing">Đang upload</Tag>;
      case 'error':
        return <Tag icon={<CloseCircleOutlined />} color="error">Lỗi</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns: ColumnsType<aiAssistantService.AIDocument> = [
    {
      title: 'Tiêu đề',
      dataIndex: 'Title',
      key: 'title',
      render: (title: string, record: aiAssistantService.AIDocument) => (
        <div>
          <div style={{ fontWeight: 500 }}>{title}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.FileName}</div>
        </div>
      ),
    },
    {
      title: 'Bộ sưu tập',
      dataIndex: 'CollectionName',
      key: 'collection',
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: 'Kích thước',
      dataIndex: 'FileSize',
      key: 'fileSize',
      width: 120,
      render: (size: number) => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'status',
      width: 150,
      render: (status: string, record: aiAssistantService.AIDocument) => (
        <Tooltip title={record.ErrorMessage || ''}>
          {getStatusTag(status)}
        </Tooltip>
      ),
    },
    {
      title: 'Ngày upload',
      dataIndex: 'UploadedAt',
      key: 'uploadedAt',
      width: 180,
      render: (date: string) => {
        if (!date) return '-';
        const d = new Date(date);
        return isNaN(d.getTime()) ? '-' : d.toLocaleString('vi-VN');
      },
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: aiAssistantService.AIDocument) => (
        <Space>
          {record.Status === 'pending' && (
            <Button
              type="link"
              icon={<CloudUploadOutlined />}
              onClick={() => handleSync(record.DocumentID)}
            >
              Đồng bộ
            </Button>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Xác nhận xóa?"
            onConfirm={() => handleDelete(record.DocumentID)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Select
            style={{ width: 200 }}
            placeholder="Lọc theo bộ sưu tập"
            allowClear
            value={selectedCollection}
            onChange={setSelectedCollection}
          >
            {collections.map((col) => (
              <Select.Option key={col.CollectionID} value={col.CollectionID}>
                {col.Icon} {col.Name}
              </Select.Option>
            ))}
          </Select>
          <Badge count={documents.length} showZero>
            <Button onClick={loadDocuments}>Làm mới</Button>
          </Badge>
        </Space>

        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => setUploadModalVisible(true)}
        >
          Upload tài liệu
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={documents}
        rowKey="DocumentID"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      {/* Upload Modal */}
      <Modal
        title="Upload tài liệu"
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => {
          setUploadModalVisible(false);
          uploadForm.resetFields();
          setFileList([]);
        }}
        okText="Upload"
        cancelText="Hủy"
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="collectionId"
            label="Bộ sưu tập"
            rules={[{ required: true, message: 'Vui lòng chọn bộ sưu tập' }]}
          >
            <Select placeholder="Chọn bộ sưu tập">
              {collections.map((col) => (
                <Select.Option key={col.CollectionID} value={col.CollectionID}>
                  {col.Icon} {col.Name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="title" label="Tiêu đề">
            <Input placeholder="Để trống sẽ dùng tên file" />
          </Form.Item>

          <Form.Item label="File (PDF, tối đa 50MB)">
            <Upload
              maxCount={1}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              beforeUpload={() => false}
              accept=".pdf"
            >
              <Button icon={<UploadOutlined />}>Chọn file</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Sửa tài liệu"
        open={editModalVisible}
        onOk={handleEditOk}
        onCancel={() => setEditModalVisible(false)}
        okText="Lưu"
        cancelText="Hủy"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="collectionId"
            label="Bộ sưu tập"
            rules={[{ required: true, message: 'Vui lòng chọn bộ sưu tập' }]}
          >
            <Select>
              {collections.map((col) => (
                <Select.Option key={col.CollectionID} value={col.CollectionID}>
                  {col.Icon} {col.Name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DocumentsTab;
